import { applyCommand, type Command } from "../document/commands.ts";
import type { MapDocument, PropInstance, TileRef, WorldDocument } from "../document/types.ts";
import { cellOffset } from "./collision-snap.ts";
import { selectedProps, together } from "./many-props.ts";
import { movePropCommand } from "./panel-commands.ts";
import type { PropRef } from "./templates.ts";

/**
 * Placed props on the collision cells (ADR-040 amendment of 2026-10-03, "props stand on the cells", P1.6-T82).
 * The stamp lays every footprint with the frame's bottom-left corner, its bottom-right when mirrored, on the
 * nearest collision cell corner, and the footprint panel draws the frame with that corner on a cell corner. A
 * prop whose frame stands between cells therefore blocks cells that lie up to half a cell from its art, where
 * the panel shows them flush with it. Moving the prop by that offset, at most half a cell on each axis, puts
 * the art exactly over its collision.
 *
 * Pieces composed together move together (the author's ruling of 2026-10-03: "Move touching pieces together").
 * Props whose art boxes overlap by at least `GROUP_OVERLAP_SHARE` of the smaller box are pieces of one
 * composition, a door in its wall or a roof on its house, and such pairs chain into a group. A group moves as
 * one rigid unit by the offset that puts its anchor piece on the cells, so no piece moves against another.
 * The anchor is the member whose move onto the cells leaves the fewest members off them (the author's ruling
 * of the same day), then the fewest pixels of residual in all, then the member that blocks the most cells, the
 * largest art and the lowest id. A member whose own corner is still off the
 * cells once the group has moved keeps that residual and is reported, never torn out of the group.
 *
 * Every prop is measured alike, by its own frame (`cellOffset`): one that blocks, one that blocks nothing, one
 * that draws under the characters and one that carries a behaviour. A mirrored prop is measured by its right
 * edge and needs its frame's width; one whose width nothing knows cannot be measured, and a group anchored on
 * such a prop is left where it stands, unreported. The cell size is the tile size over the collision
 * resolution, both read from the caller, so any game's grid is honoured.
 *
 * Placing art by the pixel is the author's to do (ADR-040 amendment of 2026-10-04, P1.6-T88). A prop that
 * blocks nothing in any state has no collision for its art to be off from, so it is never reported, and
 * neither is it counted as staying off inside a group. A prop that blocks and stands off the cells is reported
 * until the author keeps it as placed (`PropInstance.keepPlacement`): it is then never reported, and the align
 * leaves the whole group it belongs to where it stands, since moving the group would move the kept prop and
 * moving the others alone would tear the composition. Where collision is stamped is not changed by any of it.
 * The warnings and the measure a compile context gives are in `prop-align-issues.ts`.
 */

/**
 * The share of the smaller of two art boxes their overlap must cover for the two props to be pieces of one
 * composition. Half keeps a building's pieces together (a door, a roof, a porch against their walls) and leaves
 * props that merely stand side by side, or whose transparent margins cross, to move on their own: with any
 * overlap at all counted, 120 of one village's 188 props chain into a single group.
 */
export const GROUP_OVERLAP_SHARE = 0.5;

/**
 * The collision cells' geometry: the tile size in pixels and the collision cells along a tile.
 */
export interface CellGrid {
  readonly tileSize: number;
  readonly cellsPerTile: number;
}

/**
 * A frame's width in pixels, undefined when nothing sizes it.
 */
export type FrameWidthOf = (frame: TileRef) => number | undefined;

/**
 * What the alignment reads of a prop beyond its anchor: its frame's pixel size, undefined when nothing sizes
 * it (it then covers one tile), and how many collision cells it blocks as placed, 0 for one that blocks
 * nothing. `blocks` is asked only where two members of a group of several would leave it alike. `collides` says
 * whether the prop blocks any cell in any state it can be in, which is what decides whether it is reported;
 * absent, a prop collides when `blocks` counts a cell.
 */
export interface PropMeasure {
  readonly sizeOf: (frame: TileRef) => { readonly w: number; readonly h: number } | undefined;
  readonly blocks: (prop: PropInstance) => number;
  readonly collides?: (prop: PropInstance) => boolean;
}

/**
 * A prop off the cells and the move in whole pixels that puts it on them.
 */
export interface PropMove extends PropRef {
  readonly dx: number;
  readonly dy: number;
}

/**
 * A placed prop with its layer.
 */
export interface PlacedProp {
  readonly layerId: string;
  readonly prop: PropInstance;
}

/**
 * The props of one composition and the member the group is aligned by.
 */
export interface PropGroup {
  readonly members: readonly PlacedProp[];
  readonly anchor: PlacedProp;
}

/**
 * How one group stands against the cells: its anchor and members, the move in whole pixels that puts the
 * anchor on the cells (zero on both axes when it is on them), the members that collide and whose own corner is
 * off the cells once the group has made that move, each with the move that would put it alone on them, and how
 * many members that collide are kept as placed: a group with any is left where it stands. The mark on a member
 * that blocks nothing counts for nothing, since no collision of its own is held by where it stands.
 */
export interface GroupAlignment {
  readonly anchor: PropRef;
  readonly members: readonly PropRef[];
  readonly dx: number;
  readonly dy: number;
  readonly residuals: readonly PropMove[];
  readonly kept: number;
}

/**
 * What an alignment does: how many props move, in how many groups, the largest move on either axis in pixels,
 * how many members stay off the cells inside their group with the largest such residual, and how many props
 * kept as placed held a group that stands off the cells where it is.
 */
export interface AlignSummary {
  readonly aligned: number;
  readonly groups: number;
  readonly largest: number;
  readonly off: number;
  readonly offLargest: number;
  readonly kept: number;
}

/**
 * What Align props to collision cells did in the editor: the summary, and how many groups that stand off the
 * cells were left because one of their members is on a locked or hidden layer.
 */
export interface AlignOutcome extends AlignSummary {
  readonly held: number;
}

/**
 * What an Issues line's fix did (`EditorStore.fixIssue`): for `align-prop` the outcome of aligning the prop's
 * group; for `align-prop-alone` whether the prop moved and the move it stood from the cells, zero on both axes
 * when it stood on them, so a prop off the cells that did not move was held by a locked or hidden layer; for
 * `keep-prop-placement` whether the prop was marked, false when a locked or hidden layer refused it or it was
 * kept already; for `remove-pull` whether the pulled frame left the world's tileset list, with the refusal's
 * words when something still shows it.
 */
export type IssueFix =
  | { readonly remedy: "align-prop"; readonly outcome: AlignOutcome }
  | { readonly remedy: "align-prop-alone"; readonly moved: boolean; readonly dx: number; readonly dy: number }
  | { readonly remedy: "keep-prop-placement"; readonly kept: boolean }
  | { readonly remedy: "remove-pull"; readonly removed: boolean; readonly refusal: string | null }
  | { readonly remedy: "remove-layer"; readonly removed: boolean };

/**
 * One map of an aligned document: how many props it holds and every group's alignment before the move.
 */
export interface MapAlignment {
  readonly mapId: string;
  readonly props: number;
  readonly groups: readonly GroupAlignment[];
}

/**
 * Whether a prop collides: the measure's own answer, or, where it gives none, whether the prop blocks a cell
 * as placed. Only a prop that collides has a place against the cells that matters, so only one is reported,
 * offered Keep as placed, or able to hold its group by being kept.
 *
 * @param prop The prop.
 * @param measure Blocked cells.
 * @returns True when the prop blocks any cell.
 */
export function collides(prop: PropInstance, measure: PropMeasure): boolean {
  return measure.collides === undefined ? measure.blocks(prop) > 0 : measure.collides(prop);
}

/**
 * Whether a prop's place against the cells is reported: it collides and is not kept as placed.
 *
 * @param prop The prop.
 * @param measure Blocked cells.
 * @returns True when a warning is owed for it once it stands off the cells.
 */
export function reported(prop: PropInstance, measure: PropMeasure): boolean {
  return prop.keepPlacement !== true && collides(prop, measure);
}

/**
 * A move in words, as a status line, an Issues line and a dry run say it: "4 px right, 3 px up", an axis that
 * does not move left out, and "0 px" for no move at all.
 *
 * @param dx Pixels right; negative is left.
 * @param dy Pixels down; negative is up.
 * @returns The words.
 */
export function moveWords(dx: number, dy: number): string {
  const across = dx === 0 ? [] : [`${Math.abs(dx)} px ${dx > 0 ? "right" : "left"}`];
  const down = dy === 0 ? [] : [`${Math.abs(dy)} px ${dy > 0 ? "down" : "up"}`];
  return across.length + down.length === 0 ? "0 px" : [...across, ...down].join(", ");
}

/**
 * How far a placed prop stands from the collision cells.
 *
 * @param prop The prop's frame, anchor and mirror.
 * @param widthOf Frame width lookup; read only for a mirrored prop.
 * @param grid The cells' geometry.
 * @returns The move in pixels that puts it on the cells, zero on both axes when it is on them, or null for a
 *   mirrored prop whose frame's width is unknown.
 */
export function propCellOffset(prop: Pick<PropInstance, "frame" | "x" | "y" | "flipX">, widthOf: FrameWidthOf, grid: CellGrid): { dx: number; dy: number } | null {
  const width = prop.flipX ? widthOf(prop.frame) : 0;
  return width === undefined ? null : cellOffset(prop, width, prop.flipX, grid.tileSize, grid.cellsPerTile);
}

/**
 * The props of a map that stand off the collision cells, each with the move that would put it alone on them.
 *
 * @param map The map.
 * @param widthOf Frame width lookup.
 * @param grid The cells' geometry.
 * @param refs The props to look at; every prop of the map when omitted.
 * @returns The moves, in layer order and each layer's order, or the order of the props given.
 */
export function offCellProps(map: MapDocument, widthOf: FrameWidthOf, grid: CellGrid, refs?: readonly PropRef[]): PropMove[] {
  const props = refs === undefined ? placedProps(map) : selectedProps(map, refs);
  return props.flatMap(({ layerId, prop }) => {
    const offset = propCellOffset(prop, widthOf, grid);
    return offset === null || (offset.dx === 0 && offset.dy === 0) ? [] : [{ layerId, propId: prop.id, dx: offset.dx, dy: offset.dy }];
  });
}

/**
 * Every prop of a map with its layer, in layer order and each layer's order.
 *
 * @param map The map.
 * @returns The props.
 */
export function placedProps(map: MapDocument): PlacedProp[] {
  return map.layers.flatMap((layer) => (layer.kind === "prop" ? layer.props.map((prop) => ({ layerId: layer.id, prop })) : []));
}

/**
 * The key of a placed prop within its map.
 *
 * @param layerId The prop's layer.
 * @param propId The prop's id.
 * @returns The key.
 */
export function keyOf(layerId: string, propId: string): string {
  return `${layerId}\n${propId}`;
}

/**
 * A member of a group as the anchor choice weighs it.
 */
interface Candidate {
  readonly placed: PlacedProp;
  readonly area: number;
}

/**
 * Whether one member outranks another where their offsets leave the group alike: it blocks more cells, else
 * its art is larger, else its id sorts first, else its layer's does.
 *
 * @param a A member.
 * @param b Another.
 * @param measure Blocked cells.
 * @returns True when `a` is the anchor of the two.
 */
function outranks(a: Candidate, b: Candidate, measure: PropMeasure): boolean {
  const blocksA = measure.blocks(a.placed.prop);
  const blocksB = measure.blocks(b.placed.prop);
  if (blocksA !== blocksB) {
    return blocksA > blocksB;
  }
  if (a.area !== b.area) {
    return a.area > b.area;
  }
  if (a.placed.prop.id !== b.placed.prop.id) {
    return a.placed.prop.id < b.placed.prop.id;
  }
  return a.placed.layerId < b.placed.layerId;
}

/**
 * What a rigid move of a group leaves behind: how many members stand off the cells after it, and the pixels
 * they are off by in all, both axes of every member summed. A member that cannot be measured counts for
 * nothing.
 *
 * @param members The group's members.
 * @param move The move in pixels.
 * @param widthOf Frame width lookup.
 * @param grid The cells' geometry.
 * @returns The members off the cells with what remains for each, and the total.
 */
function leftOff(members: readonly PlacedProp[], move: { readonly dx: number; readonly dy: number }, widthOf: FrameWidthOf, grid: CellGrid): { readonly residuals: PropMove[]; readonly pixels: number } {
  let pixels = 0;
  const residuals = members.flatMap(({ layerId, prop }) => {
    const left = propCellOffset({ frame: prop.frame, flipX: prop.flipX, x: prop.x + move.dx / grid.tileSize, y: prop.y + move.dy / grid.tileSize }, widthOf, grid);
    if (left === null || (left.dx === 0 && left.dy === 0)) {
      return [];
    }
    pixels += Math.abs(left.dx) + Math.abs(left.dy);
    return [{ layerId, propId: prop.id, dx: left.dx, dy: left.dy }];
  });
  return { residuals, pixels };
}

/**
 * The anchor of a group of several (the author's ruling of 2026-10-03: the group lines up on the member that
 * leaves the fewest members off the cells). Each member that can be measured offers the move that puts it
 * exactly on the cells; the anchor is the one whose move leaves the fewest members off them, then the one
 * leaving the fewest pixels of residual in all, then the member blocking the most cells, the largest art and
 * the lowest id. With no member that can be measured the last three alone choose, and the group does not move.
 *
 * @param candidates The members with their art areas.
 * @param measure Frame sizes and blocked cells.
 * @param grid The cells' geometry.
 * @returns The anchor.
 */
function anchorOf(candidates: readonly Candidate[], measure: PropMeasure, grid: CellGrid): PlacedProp {
  const widthOf: FrameWidthOf = (frame) => measure.sizeOf(frame)?.w;
  const members = candidates.map((candidate) => candidate.placed);
  let best: { readonly candidate: Candidate; readonly off: number; readonly pixels: number } | null = null;
  for (const candidate of candidates) {
    const move = propCellOffset(candidate.placed.prop, widthOf, grid);
    if (move === null) {
      continue;
    }
    const left = leftOff(members, move, widthOf, grid);
    const fewer = best === null || left.residuals.length < best.off || (left.residuals.length === best.off && (left.pixels < best.pixels || (left.pixels === best.pixels && outranks(candidate, best.candidate, measure))));
    if (fewer) {
      best = { candidate, off: left.residuals.length, pixels: left.pixels };
    }
  }
  return (best?.candidate ?? candidates.reduce((first, candidate) => (outranks(candidate, first, measure) ? candidate : first))).placed;
}

/**
 * The groups of a map's props (`GROUP_OVERLAP_SHARE`): pairs whose art boxes overlap by at least that share of
 * the smaller box, chained, across every prop layer; a prop no other overlaps so is a group of one. Each group
 * names its anchor (`anchorOf`): the member whose move onto the cells leaves the fewest members off them.
 *
 * @param map The map.
 * @param measure Frame sizes and blocked cells.
 * @param grid The cells' geometry.
 * @returns The groups, each in document order, ordered by their first member.
 */
export function propGroups(map: MapDocument, measure: PropMeasure, grid: CellGrid): PropGroup[] {
  const tileSize = grid.tileSize;
  const placed = placedProps(map);
  const boxes = placed.map(({ prop }) => {
    const size = measure.sizeOf(prop.frame) ?? { w: tileSize, h: tileSize };
    const left = Math.round(prop.x * tileSize);
    const bottom = Math.round(prop.y * tileSize) + tileSize;
    return { left, right: left + size.w, top: bottom - size.h, bottom, area: size.w * size.h };
  });
  const parent = placed.map((_, index) => index);
  const find = (index: number): number => {
    let root = index;
    while (parent[root] !== root) {
      root = parent[root] as number;
    }
    parent[index] = root;
    return root;
  };
  const byLeft = placed.map((_, index) => index).sort((a, b) => (boxes[a] as { left: number }).left - (boxes[b] as { left: number }).left);
  byLeft.forEach((i, at) => {
    const a = boxes[i] as (typeof boxes)[number];
    for (let next = at + 1; next < byLeft.length; next += 1) {
      const j = byLeft[next] as number;
      const b = boxes[j] as (typeof boxes)[number];
      if (b.left >= a.right) {
        break;
      }
      const width = Math.min(a.right, b.right) - b.left;
      const height = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
      if (width > 0 && height > 0 && width * height >= GROUP_OVERLAP_SHARE * Math.min(a.area, b.area)) {
        parent[find(i)] = find(j);
      }
    }
  });
  const grouped = new Map<number, number[]>();
  placed.forEach((_, index) => {
    const root = find(index);
    grouped.set(root, [...(grouped.get(root) ?? []), index]);
  });
  return [...grouped.values()].map((indices) => {
    const members = indices.map((index) => placed[index] as PlacedProp);
    if (members.length === 1) {
      return { members, anchor: members[0] as PlacedProp };
    }
    return { members, anchor: anchorOf(indices.map((index) => ({ placed: placed[index] as PlacedProp, area: (boxes[index] as { area: number }).area })), measure, grid) };
  });
}

/**
 * How the groups of a map stand against the collision cells.
 *
 * @param map The map.
 * @param measure Frame sizes and blocked cells.
 * @param grid The cells' geometry.
 * @param refs When given, only the groups holding at least one of these props: a prop brings its whole group.
 * @returns Each group's alignment, in the groups' order; a group whose anchor cannot be measured is left out.
 *   A group's residuals name only the members that collide and are not kept as placed, the ones the Issues
 *   list reports, and its `kept` counts only the members that collide and are kept as placed.
 */
export function groupAlignments(map: MapDocument, measure: PropMeasure, grid: CellGrid, refs?: readonly PropRef[]): GroupAlignment[] {
  const widthOf: FrameWidthOf = (frame) => measure.sizeOf(frame)?.w;
  const wanted = refs === undefined ? null : new Set(refs.map((ref) => keyOf(ref.layerId, ref.propId)));
  return propGroups(map, measure, grid).flatMap((group) => {
    if (wanted !== null && !group.members.some((member) => wanted.has(keyOf(member.layerId, member.prop.id)))) {
      return [];
    }
    const move = propCellOffset(group.anchor.prop, widthOf, grid);
    if (move === null) {
      return [];
    }
    const byKey = new Map(group.members.map((member) => [keyOf(member.layerId, member.prop.id), member.prop]));
    const residuals = leftOff(group.members, move, widthOf, grid).residuals.filter((residual) => reported(byKey.get(keyOf(residual.layerId, residual.propId)) as PropInstance, measure));
    const kept = group.members.filter((member) => member.prop.keepPlacement === true && collides(member.prop, measure)).length;
    return [{ anchor: { layerId: group.anchor.layerId, propId: group.anchor.prop.id }, members: group.members.map((member) => ({ layerId: member.layerId, propId: member.prop.id })), dx: move.dx, dy: move.dy, residuals, kept }];
  });
}

/**
 * Counts what aligning groups does.
 *
 * @param groups The groups, as `groupAlignments` gives them.
 * @returns How many props move in how many groups, the largest move, how many members then stay off the
 *   cells inside their group with the largest such residual, and how many props kept as placed hold a group
 *   that stands off the cells where it is. A group holding a kept prop counts for nothing else.
 */
export function alignSummary(groups: readonly GroupAlignment[]): AlignSummary {
  const free = groups.filter((group) => group.kept === 0);
  const moving = free.filter((group) => group.dx !== 0 || group.dy !== 0);
  const residuals = free.flatMap((group) => group.residuals);
  return {
    kept: groups.reduce((count, group) => count + (group.dx !== 0 || group.dy !== 0 ? group.kept : 0), 0),
    aligned: moving.reduce((count, group) => count + group.members.length, 0),
    groups: moving.length,
    largest: moving.reduce((most, group) => Math.max(most, Math.abs(group.dx), Math.abs(group.dy)), 0),
    off: residuals.length,
    offLargest: residuals.reduce((most, move) => Math.max(most, Math.abs(move.dx), Math.abs(move.dy)), 0),
  };
}

/**
 * The command that moves props by whole pixels, as one undo step.
 *
 * @param map The map.
 * @param moves Each prop's move.
 * @param tileSize Tile size in pixels.
 * @returns The command, or null with no move.
 */
export function movePropsByCommand(map: MapDocument, moves: readonly PropMove[], tileSize: number): Command | null {
  const byProp = new Map(moves.map((move) => [keyOf(move.layerId, move.propId), move]));
  return together(
    selectedProps(map, moves).flatMap(({ layerId, prop }) => {
      const move = byProp.get(keyOf(layerId, prop.id));
      return move === undefined || (move.dx === 0 && move.dy === 0) ? [] : [movePropCommand(map, layerId, prop.id, (Math.round(prop.x * tileSize) + move.dx) / tileSize, (Math.round(prop.y * tileSize) + move.dy) / tileSize)];
    }),
  );
}

/**
 * The command that aligns groups, each moved whole by its own offset, as one undo step. A group holding a prop
 * kept as placed is left where it stands.
 *
 * @param map The map.
 * @param groups The groups to move, as `groupAlignments` gives them for this map.
 * @param tileSize Tile size in pixels.
 * @returns The command, or null when no group moves.
 */
export function alignPropsCommand(map: MapDocument, groups: readonly GroupAlignment[], tileSize: number): Command | null {
  return movePropsByCommand(map, groups.flatMap((group) => (group.kept > 0 ? [] : group.members.map((member) => ({ ...member, dx: group.dx, dy: group.dy })))), tileSize);
}

/**
 * A document with every map's groups moved onto the collision cells, for a tool that aligns or measures a
 * saved world outside the editor.
 *
 * @param world The document.
 * @param measure Frame sizes and blocked cells.
 * @param cellsPerTile Collision cells per tile.
 * @returns The aligned document, the same object when nothing moves, and each map's groups as they stood.
 */
export function alignedWorld(world: WorldDocument, measure: PropMeasure, cellsPerTile: number): { readonly world: WorldDocument; readonly maps: readonly MapAlignment[] } {
  const grid: CellGrid = { tileSize: world.tileSize, cellsPerTile };
  let current = world;
  const maps = world.maps.map((map): MapAlignment => {
    const groups = groupAlignments(map, measure, grid);
    const command = alignPropsCommand(map, groups, world.tileSize);
    if (command !== null) {
      current = applyCommand(current, command);
    }
    return { mapId: map.id, props: map.layers.reduce((count, layer) => count + (layer.kind === "prop" ? layer.props.length : 0), 0), groups };
  });
  return { world: current, maps };
}
